import * as React from "react";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/lib/auth-context";
import { fetchMyTagPreferences, storyTags } from "@/lib/tags";
import type { StoryCardData } from "@/lib/queries";

/**
 * Reader hashtag preferences. Muted tags are applied to every recommendation
 * surface (home, discover, similar publications, trending); the lists
 * themselves are private to the account and never rendered publicly.
 */
export function useTagPreferences() {
  const { user } = useAuth();
  const prefs = useQuery({
    queryKey: ["tag-preferences", user?.id],
    queryFn: () => fetchMyTagPreferences(user!.id),
    enabled: Boolean(user),
    staleTime: 60_000,
  });

  const mutedSlugs = React.useMemo(
    () => new Set((prefs.data?.muted ?? []).map((tag) => tag.slug)),
    [prefs.data],
  );
  const followedSlugs = React.useMemo(
    () => new Set((prefs.data?.followed ?? []).map((tag) => tag.slug)),
    [prefs.data],
  );

  const applyMutes = React.useCallback(
    <T extends StoryCardData>(stories: T[]) =>
      mutedSlugs.size === 0
        ? stories
        : stories.filter((story) => !storyTags(story).some((tag) => mutedSlugs.has(tag.slug))),
    [mutedSlugs],
  );

  /** Followed tags float to the top of recommendation surfaces. */
  const rankByFollowed = React.useCallback(
    <T extends StoryCardData>(stories: T[]) =>
      followedSlugs.size === 0
        ? stories
        : [...stories].sort((a, b) => {
            const score = (story: T) =>
              storyTags(story).filter((tag) => followedSlugs.has(tag.slug)).length;
            return score(b) - score(a);
          }),
    [followedSlugs],
  );

  return {
    followed: prefs.data?.followed ?? [],
    muted: prefs.data?.muted ?? [],
    mutedSlugs,
    followedSlugs,
    applyMutes,
    rankByFollowed,
  };
}
